import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input, OnChanges, SimpleChanges, SimpleChange } from '@angular/core';
import {Entity} from '../../entity/entity';
import {EntityLine} from '../../entity/entity-line';
import {RatingUnit} from '../../model/rating-unit';
import {TagInformation} from '../../model/tag-information';
import { Router } from '@angular/router';
import {ScheduleService} from '../../services/schedule.service';
import {RatingUnitService} from '../../services/rating-unit.service';
import {StringUtils} from '../../utils/string-utils';
import {EntityLineService} from '../../services/entity-line-service';
import {RatingScheduleLineService} from '../../services/rating-schedule-line.service';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {RatingsToolFormComponent} from '../ratings-tool-form/ratings-tool-form.component';

import {MatIcon} from '@angular/material/icon';
import {MatButton} from '@angular/material/button';

@Component({
    selector: 'app-unit-detail',
    templateUrl: './unit-detail.component.html',
    styleUrls: ['./unit-detail.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatButton, MatIcon, RatingsToolFormComponent, MatProgressSpinner]
})
export class UnitDetailComponent implements OnChanges, OnInit {
  private readonly router = inject(Router);
  private readonly scheduleService = inject(ScheduleService);
  private readonly ratingUnitService = inject(RatingUnitService);
  private readonly entityLineService = inject(EntityLineService);
  private readonly ratingScheduleLineService = inject(RatingScheduleLineService);
  readonly lineIdParam = input<string>(undefined!, { alias: 'lineId' });
  readonly scheduleIdParam = input<string>(undefined!, { alias: 'scheduleId' });
  readonly unitIdParam = input<string>(undefined!, { alias: 'unitId' });
  private readonly unitId = signal<number>(undefined!);
  private readonly scheduleId = signal<string>(undefined!);
  private readonly lineId = signal<string>(undefined!);
  readonly schedule = signal<Entity>(undefined!);
  readonly scheduleLine = signal<EntityLine>(undefined!);
  readonly unitToUpdate = signal<RatingUnit>(undefined!);
  updatedUnit!: RatingUnit;
  readonly saveButtonText = signal<string>(undefined!);
  tagInformation!: TagInformation[];
  quantity!: number;
  readonly isBusy = signal<boolean>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  readonly dataLoaded = signal<boolean>(false);

  private handleError(error: any): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  ngOnInit(): void {
    this.initialize();
    this.errorMessage.set(undefined!);
    this.isBusy.set(true);
    if (StringUtils.isEmptyNumericValue(this.unitId())) {
      if (this.scheduleId()) {
        this.scheduleService.getSchedule(this.scheduleId()).subscribe({
          next: (schedule) => {
            this.schedule.set(schedule);
            if (this.lineId() && '0' !== this.lineId()) {
              this.saveButtonText.set('Update Schedule Unit');
              this.entityLineService.getEntireEntityLine('QUOTE', this.scheduleId(), 0, this.lineId()).subscribe({
                next: (line) => {
                  this.scheduleLine.set(line);
                  this.isBusy.set(false);
                  this.dataLoaded.set(true);
                }, error: () => this.goBack()
              });
            } else {
              this.saveButtonText.set('Add Unit to Schedule');
              this.dataLoaded.set(true);
              this.isBusy.set(false);
            }
          }, error: () => this.goBack()
        });
      } else {
        this.router.navigate(['ratings']);
      }
    } else {
      this.saveButtonText.set('Update Unit');
      this.ratingUnitService.getUnit(this.unitId()).subscribe({
        next: (unit) => {
          unit.response = undefined!;
          this.unitToUpdate.set(unit);
          this.isBusy.set(false);
          this.dataLoaded.set(true);
        }, error: () => this.goBack()
      });
    }
  }

  goBack(): void {
    if (this.scheduleId()) {
      this.router.navigate(['schedules', this.scheduleId()]);
    } else {
      this.router.navigate(['ratings', 'unitList']);
    }
  }

  canSave(): boolean {
    if (this.isBusy()) {
      return false;
    }
    if (this.scheduleLine() || this.schedule()) {
      if (this.quantity === 0) {
        return false;
      }
    }
    return (this.updatedUnit?.response?.PrimaryCoilResponse?.TotalCapacity || 0) > 0 || (this.updatedUnit?.response?.ElectricHeatResponse?.LeavingDryBulbTemperature || 0) > 0;
  }

  save(): void {
    this.errorMessage.set(undefined!);
    if (this.canSave()) {
      this.isBusy.set(true);
      if (!StringUtils.isEmptyNumericValue(this.unitId())) {
        this.ratingUnitService.saveUnit(this.updatedUnit).subscribe({
          next: () => this.goBack(),
          error: (error) => this.handleError(error),
        });
      } else if (this.scheduleId()) {
        if (this.lineId() && '0' !== this.lineId()) {
          this.ratingScheduleLineService.updateScheduleLine(this.scheduleLine(), this.updatedUnit, this.tagInformation, this.quantity).subscribe({
            next: () => this.goBack(),
            error: (error) => this.handleError(error),
          });
        } else {
          this.ratingScheduleLineService.addScheduleLine(this.schedule(), this.updatedUnit, this.tagInformation, this.quantity).subscribe({
            next: () => this.goBack(),
            error: (error) => this.handleError(error),
          });
        }
      } else {
        this.goBack();
      }
    }
  }

  updateTags(tags: TagInformation[]): void {
    this.tagInformation = tags;
    this.quantity = tags.length;
  }


  private initialize(): void {
    this.unitId.set(+this.unitIdParam());
    this.scheduleId.set(this.scheduleIdParam());
    this.lineId.set(this.lineIdParam());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (Object.values(changes).some((change: SimpleChange) => !change.firstChange)) {
      this.initialize();
    }
  }
}
